import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import './dashboard.css';
import { Doughnut, Bar } from 'react-chartjs-2';
import 'chart.js/auto';

/* ---------- helpers ---------- */

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};



const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

const isThisMonth = (d) => {
  const now = new Date();
  const x = new Date(d);
  return (
    x.getFullYear() === now.getFullYear() &&
    x.getMonth() === now.getMonth()
  );
};

const monthIndex = (d) => new Date(d).getMonth();

const initials = (name = '?') =>
  name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatInTZ = (date, timeZone, options = {}) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    ...options,
  }).format(date);

const getTZAbbrev = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(date);
  const tz = parts.find((p) => p.type === 'timeZoneName');
  return tz ? tz.value : '';
};

const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/* Resolve invoice id in a tolerant way */
const getInvoiceId = (inv = {}) =>
  inv.invoiceId ||
  inv._id ||
  inv.id ||
  inv.invoice_id ||
  (inv.invoice && (inv.invoice._id || inv.invoice.id)) ||
  null;

/* Calculate an invoice total using invoice fields */
const getInvoiceTotal = (inv = {}) => {
  const base =
    safeNum(inv.serviceTotal) +
    safeNum(inv.productsTotal) +
    safeNum(inv.miscTotal);

  const tax = base * (safeNum(inv.taxPercent) / 100);
  const tip = safeNum(inv.tipAmount);

  const total = base + tax + tip;
  return Number.isFinite(total) ? total : 0;
};

/**
 * Dashboard
 *
 * Index.jsx should pass:
 *  - onOpenCalendarDay(date)
 *  - onOpenCalendarMonth()
 *  - onOpenRevenue()
 *  - onOpenInvoice(invoiceId)
 */
const Dashboard = ({
  onOpenCalendarDay,
  onOpenCalendarMonth,
  onOpenRevenue,
  onOpenInvoice,
}) => {
  const [stats, setStats] = useState({
    todayCount: 0,
    monthCount: 0,
    monthRevenue: 0,
    completedThisMonth: 0,
    noShowThisMonth: 0,
  });
  const [upcoming, setUpcoming] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [serviceTally, setServiceTally] = useState({});
  const [monthlyCounts, setMonthlyCounts] = useState(
    new Array(12).fill(0)
  );
  const [checkingAuth, setCheckingAuth] = useState(true);

  /* ---------- load data ---------- */
 const navigate = useNavigate();
  useEffect(() => {
   
    const load = async () => {
      try {
        const storedUserRaw = localStorage.getItem('user');
        if (!storedUserRaw) {
          navigate('/login');       // ← Redirect immediately
          return;
        }

        const storedUser = JSON.parse(storedUserRaw);
        const role = storedUser.role;

        const businessId =
          role === 'admin'
            ? storedUser.id
            : storedUser.business_id;

        const employeeId =
          role === 'employee' ? storedUser.id : null;

        if (!businessId && !employeeId) {
          setCheckingAuth(false);
          return;
        }

        const bookingsUrl = employeeId
          ? `/auth/getAllBookings?employeeId=${employeeId}`
          : `/auth/getAllBookings?businessId=${businessId}`;

        const [
          bookingsRes,
          servicesRes,
          contactsRes,
          invoicesResRaw,
        ] = await Promise.all([
          API.get(bookingsUrl),
          API.get(`/auth/services/business/${businessId}`),
          API.get(
            `/auth/contacts?businessId=${businessId}`
          ).catch(() => ({ data: [] })),
          API.get(
            `/auth/invoices?businessId=${businessId}`
          ).catch((err) => {
            if (
              err.response &&
              err.response.status === 404
            ) {
              return { data: [] };
            }
            throw err;
          }),
        ]);

        const bookings = bookingsRes.data || [];
        const services = servicesRes.data || [];
        const contacts = contactsRes.data || [];
        const invoicesRaw = invoicesResRaw.data || [];

        const serviceMap = new Map(
          services.map((s) => [
            String(s._id),
            s.name,
          ])
        );
        const contactMap = new Map(
          contacts.map((c) => [
            String(c._id),
            c.name,
          ])
        );

        const now = new Date();
        const monthly = new Array(12).fill(0);
        const serviceTallyLocal = {};
        let todayCount = 0;
        let monthCount = 0;
        let completedThisMonth = 0;
        let noShowThisMonth = 0;

        // Hydrate bookings & compute stats
        const hydratedBookings = bookings.map((b) => {
          const dt = new Date(
            b.bookingDateTime ||
            b.date ||
            b.startTime ||
            b.start
          );

          const status = (b.status || '')
            .toLowerCase()
            .trim();

          const contactId =
            b.contactId || b.contact || b.customerId;
          const serviceIds = Array.isArray(
            b.serviceIds
          )
            ? b.serviceIds
            : b.serviceId
              ? [b.serviceId]
              : [];

          if (!isNaN(dt)) {
            // Always count this booking in its calendar-month bucket
            const mi = monthIndex(dt);
            if (mi >= 0 && mi < 12) {
              monthly[mi] += 1;
            }

            // Today's count
            if (sameDay(dt, now)) {
              todayCount += 1;
            }

            // This-month-specific stats
            if (isThisMonth(dt)) {
              monthCount += 1;

              if (
                status.includes('complete') ||
                status.includes('paid') ||
                status.includes('billed')
              ) {
                completedThisMonth += 1;
              } else if (
                status.includes('no-show') ||
                status.includes('noshow')
              ) {
                noShowThisMonth += 1;
              }
            }
          }

          // Tally services
          serviceIds.forEach((sid) => {
            if (!sid) return;
            const key = String(sid);
            serviceTallyLocal[key] =
              (serviceTallyLocal[key] ||
                0) + 1;
          });

          const contactName =
            b.contactName ||
            b.customerName ||
            (contactId &&
              contactMap.get(
                String(contactId)
              )) ||
            'Guest';

          const firstServiceId =
            serviceIds[0];
          const serviceName =
            b.serviceName ||
            (firstServiceId &&
              serviceMap.get(
                String(firstServiceId)
              )) ||
            'Service';

          return {
            ...b,
            bookingDateTime: !isNaN(dt)
              ? dt.toISOString()
              : null,
            contactName,
            serviceName,
          };
        });

        // Upcoming bookings
        const upcomingHydrated = hydratedBookings
          .filter(
            (b) =>
              b.bookingDateTime &&
              new Date(
                b.bookingDateTime
              ) >= now
          )
          .sort(
            (a, b) =>
              new Date(a.bookingDateTime) -
              new Date(b.bookingDateTime)
          )
          .slice(0, 10);

        // Service tally with names
        const serviceTallyNamed = {};
        Object.entries(serviceTallyLocal).forEach(
          ([sid, count]) => {
            const name =
              serviceMap.get(sid) ||
              'Unknown Service';
            serviceTallyNamed[name] =
              (serviceTallyNamed[name] ||
                0) + count;
          }
        );

        // Hydrate invoices & attach customerName
        const invoicesHydrated = invoicesRaw
          .map((inv) => {
            const id =
              getInvoiceId(inv);
            if (!id) return null;

            const created = new Date(
              inv.createdAt ||
              inv.issueDate ||
              inv.updatedAt ||
              Date.now()
            );

            const contactId =
              inv.contactId ||
              (inv.contact &&
                inv.contact._id);
            const customerName =
              inv.customerName ||
              (contactId &&
                contactMap.get(
                  String(
                    contactId
                  )
                )) ||
              'Unknown Customer';

            return {
              ...inv,
              invoiceId: id,
              createdAt:
                !isNaN(created)
                  ? created.toISOString()
                  : null,
              customerName,
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            const da = new Date(
              a.createdAt
            );
            const db = new Date(
              b.createdAt
            );
            return db - da;
          })
          .slice(0, 4);

        // Month revenue from ALL invoices
        let monthRevenue = 0;
        invoicesRaw.forEach((inv) => {
          const dt = new Date(
            inv.createdAt ||
            inv.issueDate ||
            inv.updatedAt ||
            Date.now()
          );
          if (isThisMonth(dt)) {
            monthRevenue +=
              getInvoiceTotal(inv);
          }
        });

        setStats({
          todayCount,
          monthCount,
          monthRevenue,
          completedThisMonth,
          noShowThisMonth,
        });
        setUpcoming(upcomingHydrated);
        setInvoices(invoicesHydrated);
        setServiceTally(serviceTallyNamed);
        setMonthlyCounts(monthly);
        setCheckingAuth(false);
      } catch (err) {
        console.error(
          'Error loading dashboard:',
          err
        );
        setCheckingAuth(false);
      }
    };

    load();
  }, []);

  if (checkingAuth) return null;

  /* ---------- chart data ---------- */

  const donutData = [
    Math.max(0, stats.completedThisMonth),
    Math.max(0, stats.noShowThisMonth),
  ];

  const donut = {
    labels: ['Completed', 'No-Show'],
    datasets: [
      {
        data:
          donutData[0] === 0 &&
            donutData[1] === 0
            ? [1, 0] // empty state
            : donutData,
        backgroundColor: [
          '#6C63FF',
          '#E5E7EB',
        ],
        hoverBackgroundColor: [
          '#5B5FEF',
          '#E5E7EB',
        ],
        borderWidth: 0,
      },
    ],
  };

  const donutOpts = {
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: { legend: { display: false } },
  };

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const monthlyBar = {
    labels: months,
    datasets: [
      {
        label: 'Monthly Bookings',
        data: monthlyCounts,
        backgroundColor: '#3B47CE',
        borderRadius: 6,
        barThickness: 26,
      },
    ],
  };

  const monthlyBarOpts = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
    },
    scales: {
      x: {
        grid: { color: '#EDF2F7' },
        ticks: { color: '#6B7280' },
      },
      y: {
        grid: { color: '#EDF2F7' },
        ticks: {
          precision: 0,
          color: '#6B7280',
        },
      },
    },
  };

  const topServices = Object.entries(
    serviceTally
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const maxVal =
    topServices.length > 0
      ? Math.max(
        ...topServices.map(
          ([, v]) => v
        )
      )
      : 1;

  const trend = (n) =>
    n <= 0
      ? '+0%'
      : `+${Math.min(
        99,
        Math.round(n * 3)
      )}%`;

  /* ---------- handlers ---------- */

  const handleTodayBookingsClick = () => {
    if (onOpenCalendarDay) {
      onOpenCalendarDay(new Date());
    }
  };

  const handleThisMonthBookingsClick = () => {
    if (onOpenCalendarMonth) {
      onOpenCalendarMonth();
    }
  };

  const handleMonthRevenueClick = () => {
    if (onOpenRevenue) {
      onOpenRevenue();
    }
  };

  const handleUpcomingRowClick = (booking) => {
    if (!onOpenCalendarDay || !booking)
      return;
    if (!booking.bookingDateTime)
      return;
    onOpenCalendarDay(
      new Date(booking.bookingDateTime)
    );
  };

  const handleInvoiceClick = (invoice) => {
    if (!onOpenInvoice || !invoice)
      return;
    const id = getInvoiceId(invoice);
    if (!id) {
      console.warn(
        'No invoice id on clicked invoice row',
        invoice
      );
      return;
    }
    onOpenInvoice(id); // Index.jsx -> InvoicePage createdInvoiceId
  };

  /* ---------- UI ---------- */

  return (
    <main className="dashboard-main">
      {/* KPI band */}
      <section className="kpi-band">
        <div className="kpi-grid">
          {/* TODAY'S BOOKINGS */}
          <article
            className="kpi-card"
            onClick={
              handleTodayBookingsClick
            }
          >
            <h4>Today's Bookings</h4>
            <div className="kpi-value">
              {stats.todayCount}
            </div>
            <div className="kpi-line">
              <span />
            </div>
            <div className="kpi-micro">
              ↑{' '}
              {trend(
                stats.todayCount
              )}{' '}
              this week
            </div>
          </article>

          {/* THIS MONTH — BOOKINGS */}
          <article
            className="kpi-card"
            onClick={
              handleThisMonthBookingsClick
            }
          >
            <h4>This Month — Bookings</h4>
            <div className="kpi-value">
              {stats.monthCount}
            </div>
            <div className="kpi-line">
              <span
                style={{
                  width: '72%',
                }}
              />
            </div>
            <div className="kpi-micro">
              ↑{' '}
              {trend(
                stats.monthCount
              )}{' '}
              this week
            </div>
            <div className="kpi-sub">
              Opens calendar in month
              view
            </div>
          </article>

          {/* THIS MONTH — REVENUE */}
          <article
            className="kpi-card"
            onClick={
              handleMonthRevenueClick
            }
          >
            <h4>This Month — Revenue</h4>
            <div className="kpi-value">
              $
              {stats.monthRevenue.toLocaleString()}
            </div>
            <div className="kpi-line">
              <span
                style={{
                  width: '54%',
                }}
              />
            </div>
            <div className="kpi-micro success">
              ↑{' '}
              {trend(
                Math.round(
                  stats
                    .monthRevenue /
                  75
                )
              )}{' '}
              this week
            </div>
            <div className="kpi-sub">
              Opens revenue view
            </div>
          </article>
        </div>
      </section>

      {/* Upcoming + Recent Invoices */}
      <section className="row-above">
        {/* UPCOMING BOOKINGS */}
        <article className="card">
          <div className="row-between">
            <h4>Upcoming Bookings</h4>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Name</th>
                <th>Service</th>
                <th>Employee</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.map(
                (b, i) => {
                  const dt =
                    b.bookingDateTime
                      ? new Date(
                        b.bookingDateTime
                      )
                      : null;
                  const dateStr =
                    dt &&
                    formatInTZ(
                      dt,
                      'America/New_York',
                      {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      }
                    );
                  const timeStr =
                    dt &&
                    formatInTZ(
                      dt,
                      'America/New_York',
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      }
                    );
                  const tzAbbr =
                    dt &&
                    getTZAbbrev(
                      dt,
                      'America/New_York'
                    );
                  const status =
                    b.status ||
                    'Pending';
                  const good =
                    /complete|billed|paid/i.test(
                      status
                    );

                  return (
                    <tr
                      key={i}
                      onClick={() =>
                        handleUpcomingRowClick(
                          b
                        )
                      }
                    >
                      <td>
                        {dateStr ||
                          '-'}
                      </td>
                      <td>
                        {timeStr &&
                          tzAbbr
                          ? `${timeStr} ${tzAbbr}`
                          : '-'}
                      </td>
                      <td>
                        {b.contactName}
                      </td>
                      <td>
                        {b.serviceName}
                      </td>
                      <td>
                        {b.employeeName ||
                          '-'}
                      </td>
                      <td>
                        <span
                          className={`badge ${good
                              ? 'success'
                              : 'warn'
                            }`}
                        >
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                }
              )}
              {upcoming.length ===
                0 && (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        textAlign:
                          'center',
                        padding:
                          '12px',
                      }}
                    >
                      No upcoming
                      bookings.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </article>

        {/* RECENT INVOICES */}
        <article className="card">
          <h4>Recent Invoices</h4>
          <table className="inv-table">
            <thead>
              <tr>
                <th>
                  Customer / Invoice
                </th>
                <th className="right-align">
                  Amount
                </th>
                <th className="right-align">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(
                (inv, idx) => {
                  const id =
                    getInvoiceId(
                      inv
                    );
                  const dt =
                    inv.createdAt
                      ? new Date(
                        inv.createdAt
                      )
                      : null;
                  const dateStr =
                    dt &&
                    formatInTZ(
                      dt,
                      'America/New_York',
                      {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      }
                    );
                  const tzAbbr =
                    dt &&
                    getTZAbbrev(
                      dt,
                      'America/New_York'
                    );
                  const amount =
                    getInvoiceTotal(
                      inv
                    );
                  const displayName =
                    inv.customerName ||
                    inv.contactName ||
                    `Invoice #${String(
                      id || ''
                    ).slice(
                      -6
                    )}`;

                  return (
                    <tr
                      key={idx}
                      className="inv-row"
                      onClick={() =>
                        handleInvoiceClick(
                          inv
                        )
                      }
                      style={{
                        cursor: id
                          ? 'pointer'
                          : 'default',
                      }}
                    >
                      <td className="inv-cell">
                        <div className="person">
                          <div className="avatar">
                            {initials(
                              displayName
                            )}
                          </div>
                          <div className="name">
                            {displayName}
                          </div>
                        </div>
                      </td>
                      <td className="inv-cell right-align">
                        $
                        {amount.toLocaleString()}
                      </td>
                      <td className="inv-cell right-align">
                        {dateStr &&
                          tzAbbr
                          ? `${dateStr} ${tzAbbr}`
                          : '-'}
                      </td>
                    </tr>
                  );
                }
              )}
              {invoices.length ===
                0 && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        textAlign:
                          'center',
                        padding:
                          '12px',
                      }}
                    >
                      No invoices
                      found.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </article>
      </section>

      {/* Bottom: Monthly stats + Top services + Donut */}
      <section className="db-grid">
        <div className="left-col">
          <article className="card monthly">
            <h4>Monthly Booking Stats</h4>
            <div className="chart-wrap">
              <Bar
                data={monthlyBar}
                options={monthlyBarOpts}
              />
            </div>
          </article>

          <article className="card services">
            <h4>Top Services This Month</h4>
            <div className="hr" />
            <div className="service-list">
              {topServices.map(
                (
                  [label, val],
                  i
                ) => {
                  const pct =
                    Math.round(
                      (val /
                        maxVal) *
                      100
                    );
                  return (
                    <div
                      className="service-row"
                      key={i}
                    >
                      <div className="label">
                        {label}
                      </div>
                      <div className="bar">
                        <div
                          className="fill"
                          style={{
                            width: `${pct}%`,
                          }}
                        />
                      </div>
                      <div className="value">
                        {val}
                      </div>
                    </div>
                  );
                }
              )}
              {topServices.length ===
                0 && (
                  <p className="center subtle">
                    No service data
                    for this
                    month.
                  </p>
                )}
            </div>
          </article>
        </div>

        <article className="card donut">
          <h4>
            This Month — Bookings
          </h4>
          <div className="donut-wrap">
            <Doughnut
              data={donut}
              options={donutOpts}
            />
          </div>
          <p className="center">
            Completed vs No-Show
          </p>
          <button
            className="btn ghost w-full"
            onClick={
              handleMonthRevenueClick
            }
          >
            Open detailed revenue
            report
          </button>
        </article>
      </section>
    </main>
  );
};

export default Dashboard;
